<template>
  <div class="container">
    <PulseLoader :loading="!mapIsLoaded && !error" class="spinner-overview" color="#1d234f" />
    <div v-if="error" class="spinner-overview">{{ error }}</div>
    <MapContainer v-if="coordinatesForBuildings.length" :initCoordinates="coordinatesForBuildings">
      <MapCamera />
      <MapControls />
      <MapViewLayers
        v-if="isMapviewShowing || isDashboardviewActiveFromMapview"
        :complexSource="complexSource"
        :buildingSource="buildingSource"
        :realEstateSource="realEstateSource"
      />
      <PlanViewLayers
        v-if="isPlanviewShowing || isModelviewShowing || isDashboardviewActiveFromPlanview"
      />
      <ModelViewLayers v-if="isModelviewShowing && has3dModelAccess" />
      <MeasureLayers v-if="isMeasurementActive" />
      <MapSky v-if="isMapviewShowing" />
    </MapContainer>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex'
import {
  SET_CLICKED_ITEM_TYPE,
  CHANGE_PRIMARY_INSTANCE,
  SET_FILTERED_MAP_POI_LAYERS,
  SET_STATUS_ON_SHOW_OVERLAY_MODAL,
  SET_CURRENT_OVERLAY_MODAL,
  SET_POI_CATEGORIES_FOR_SELECTED_BUILDING
} from '@/store/mutation-types'
import { INDOORVIEWER_PREFIX, PLANVIEW_PREFIX, MAPVIEW_PREFIX } from '@/store/module-prefixes'
import {
  CHANGE_ROOM,
  CHANGE_LEVEL,
  CHANGE_BUILDING,
  CHANGE_REAL_ESTATE,
  CHANGE_COMPLEX,
  CHANGE_POI,
  GET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE,
  GET_OUTDOOR_ZONE_DATA_FOR_REAL_ESTATE,
  GET_INDOOR_ZONE_DATA_FOR_LEVEL
} from '@/store/action-types'
import { getIdFromQuery } from '@/utils/query-param-utils'
import MapContainer from '@/components/map/MapContainer.vue'
import MapCamera from '@/components/map/MapCamera.vue'
import MapControls from '@/components/map/controls/MapControls.vue'
import MapSky from '@/components/map/MapSky.vue'
import MapViewLayers from '@/components/map/layers/mapview-layers/MapViewLayers.vue'
import PlanViewLayers from '@/components/map/layers/planview-layers/PlanViewLayers.vue'
import ModelViewLayers from '@/components/map/layers/modelview-layers/ModelViewLayers.vue'
import MeasureLayers from '@/components/map/layers/measure-layers/MeasureLayers.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import { getPoiById } from '@/services/pois.service'
import { setViewToIndoorview, setViewToModelview, setViewToPlanview } from '../../utils/views'
import { mapState as mapStatePinia } from 'pinia'
import { useGeneralStore } from '@/pinia'
import { getLineLayerById } from '@/services/lines.service'
import { getZoneLayerById } from '@/services/zones.service'
import { useSelectionStore } from '@/pinia/selectionNew'

export default {
  name: 'MapView',
  components: {
    MapContainer,
    MapCamera,
    MapControls,
    MapSky,
    MapViewLayers,
    PlanViewLayers,
    ModelViewLayers,
    MeasureLayers,
    PulseLoader
  },
  props: {
    error: {
      validator: (prop) => typeof prop === 'string' || prop === null,
      default: null
    },
    initialFetchLoading: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { setSelectedLine, setSelectedZone, updateLineDataForCoreEntity } = useGeneralStore()
    return {
      setSelectedLine,
      setSelectedZone,
      updateLineDataForCoreEntity
    }
  },
  data() {
    return {
      isSpinnerShowing: true,
      coordinatesForBuildings: []
    }
  },
  computed: {
    ...mapGetters({
      has3dModelAccess: 'has3dModelAccess',
      hasIndoorviewAccess: 'hasIndoorviewAccess',
      hasLineReaderAccess: 'hasLineReaderAccess',
      hasPoiReaderAccess: 'hasPoiReaderAccess',
      hasZoneAccess: 'hasZoneAccess',
      isMapviewShowing: 'isMapviewShowing',
      isPlanviewShowing: 'isPlanviewShowing',
      isModelviewShowing: 'isModelviewShowing',
      isDashboardviewActiveFromMapview: 'isDashboardviewActiveFromMapview',
      isDashboardviewActiveFromPlanview: 'isDashboardviewActiveFromPlanview',
      isMeasurementActive: 'isMeasurementActive',
      selectedLevelId: 'selectedLevelId'
    }),
    ...mapState({
      mapIsLoaded: (state) => state.mapIsLoaded,
      buildingSource: (state) => state.buildingSource,
      complexSource: (state) => state.complexSource,
      realEstateSource: (state) => state.realEstateSource
    }),
    ...mapStatePinia(useGeneralStore, ['linesForSelectedRealEstate']),
    ...mapState(PLANVIEW_PREFIX, {
      poiCategoriesForSelectedLevel: (state) => state.poiCategoriesForSelectedLevel,
      zonesForLevel: (state) => state.indoorZonesForSelectedLevel
    }),
    ...mapState(MAPVIEW_PREFIX, {
      outdoorPoisForSelectedRealEstate: (state) => state.outdoorPoisForSelectedRealEstate,
      filteredMapPoiLayers: (state) => state.filteredPoiLayers,
      outdoorZonesForSelectedRealEstate: (state) => state.outdoorZonesForSelectedRealEstate
    }),
    ...mapGetters(MAPVIEW_PREFIX, {
      selectedRealEstateId: 'selectedRealEstateId'
    })
  },
  watch: {
    async initialFetchLoading() {
      if (this.error || this.initialFetchLoading) return
      await this.setDataFromQuery()
      this.coordinatesForBuildings = this.getBuildingCoordinates()
    }
  },
  methods: {
    ...mapActions({
      commitRoom: CHANGE_ROOM,
      changeLevel: CHANGE_LEVEL,
      dispatchBuilding: CHANGE_BUILDING,
      dispatchRealEstate: CHANGE_REAL_ESTATE,
      dispatchComplex: CHANGE_COMPLEX,
      dispatchPoi: CHANGE_POI
    }),
    ...mapActions(PLANVIEW_PREFIX, {
      loadSensorData: 'loadSensorData',
      getZoneDataForLevel: GET_INDOOR_ZONE_DATA_FOR_LEVEL
    }),
    ...mapMutations({
      commitClick: SET_CLICKED_ITEM_TYPE,
      toggleOverlayModal: SET_STATUS_ON_SHOW_OVERLAY_MODAL,
      setCurrentOverlayModal: SET_CURRENT_OVERLAY_MODAL
    }),
    ...mapMutations(INDOORVIEWER_PREFIX, {
      changePrimaryInstance: CHANGE_PRIMARY_INSTANCE
    }),
    ...mapActions(MAPVIEW_PREFIX, {
      getOutdoorPoiDataForRealEstate: GET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE,
      getOutdoorZoneDataForRealEstate: GET_OUTDOOR_ZONE_DATA_FOR_REAL_ESTATE
    }),
    ...mapMutations(MAPVIEW_PREFIX, {
      setFilteredMapPoiLayers: SET_FILTERED_MAP_POI_LAYERS,
      setPoiCategoriesForSelectedBuilding: SET_POI_CATEGORIES_FOR_SELECTED_BUILDING
    }),
    getBuildingCoordinates() {
      const buildingCoordinates = []
      buildingCoordinates.push(
        ...this.buildingSource.data.features
          .filter((x) => x.geometry)
          .filter((x) => x.geometry.type === 'Polygon')
          .map((x) => x.geometry.coordinates)
          .flatMap((x) => x[0])
      )
      buildingCoordinates.push(
        ...this.buildingSource.data.features
          .filter((x) => x.geometry)
          .filter((x) => x.geometry.type === 'MultiPolygon')
          .map((x) => x.geometry.coordinates)
          .flatMap((x) => x[0][0])
      )
      return buildingCoordinates
    },
    async setDataFromQuery() {
      const selection = useSelectionStore()

      const complexId = getIdFromQuery(location.search, 'complex')
      const realEstateId = getIdFromQuery(location.search, 'realEstate')
      const buildingId = getIdFromQuery(location.search, 'building')
      const levelNumber = getIdFromQuery(location.search, 'levelNumber')
      const levelId = getIdFromQuery(location.search, 'level')
      const roomId = getIdFromQuery(location.search, 'room')
      const poiId = getIdFromQuery(location.search, 'poi')
      const lineLayerId = getIdFromQuery(location.search, 'lineLayer')
      const zoneLayerId = getIdFromQuery(location.search, 'zoneLayer')
      // const errandId = getIdFromQuery(location.search, 'errand')
      const instanceNumber = getIdFromQuery(location.search, 'instance')
      const isPlanviewInQuery = location.search.includes('planview')
      const isModelviewInQuery = location.search.includes('modelview')
      const isIndoorviewInQuery = location.search.includes('indoorview')

      // TODO: use levelNumber to set level
      const ids = [[roomId, levelId, buildingId, realEstateId, complexId].find((x) => x)]
      selection.set(ids, false)

      if (complexId) {
        this.setComplexFromQuery(complexId)
        this.commitClick({ type: 'complex' })
      } else if (realEstateId) {
        await this.setRealEstateFromQuery(realEstateId)
        this.commitClick({ type: 'realEstate' })
        if (poiId && this.hasPoiReaderAccess) {
          await this.updateAndSetOutdoorPoi(poiId, realEstateId)
          this.commitClick({ type: 'poi' })
        }
        if (lineLayerId && this.hasLineReaderAccess && !buildingId) {
          await this.updateAndSetLine(lineLayerId)
          this.commitClick({ type: 'line' })
        }
        if (zoneLayerId && this.hasZoneAccess) {
          await this.updateAndSetOutdoorZone(zoneLayerId)
          this.commitClick({ type: 'zone' })
        }
      } else if (buildingId) {
        await this.setBuildingFromQuery(buildingId)
        if (levelNumber) {
          await this.changeLevel({ levelNumber })
          if (isPlanviewInQuery) {
            setViewToPlanview()
            this.loadSensorData()
          }
          if (isModelviewInQuery) setViewToModelview()
          else if (isIndoorviewInQuery && this.hasIndoorviewAccess) {
            if (instanceNumber) {
              const instanceNr = parseInt(instanceNumber)
              this.changePrimaryInstance(instanceNr)
            }
            if (poiId && this.hasPoiReaderAccess) {
              await this.updateAndSetIndoorPoi(poiId)
              this.commitClick({ type: 'poi' })
            }
            setViewToIndoorview()
          }
          if (
            zoneLayerId &&
            this.hasZoneAccess &&
            !isIndoorviewInQuery &&
            (isPlanviewInQuery || isModelviewInQuery)
          ) {
            await new Promise((resolve) => setTimeout(resolve, 2000)) // waiting for filtered layers to be set to enable selecting zone
            await this.updateAndSetIndoorZone(zoneLayerId)
            this.commitClick({ type: 'zone' })
          } else if (poiId && this.hasPoiReaderAccess && !isIndoorviewInQuery) {
            await this.updateAndSetIndoorPoi(poiId)
            this.commitClick({ type: 'poi' })
          } else if (roomId) {
            await this.commitRoom({ roomId })
            this.commitClick({ type: 'room' })
          } else this.commitClick({ type: 'level' })
        } else this.commitClick({ type: 'building' })
      }
      // if (errandId) this.updateAndSetErrandAndInfoDisplay(errandId)
    },
    // async updateAndSetErrandAndInfoDisplay(errandId) {
    //   const errand = await getErrandById(errandId)
    //   const categories = await getCategoriesForBuilding(errand.constructionId)
    //   if (categories && this.hasPoiReaderAccess)
    //     this.setPoiCategoriesForSelectedBuilding(categories)
    // },
    async updateAndSetLine(lineLayerId) {
      const lineLayer = await getLineLayerById(lineLayerId)
      if (!lineLayer) return
      await this.updateLineDataForCoreEntity(
        lineLayer.categoryId,
        lineLayer.typeId,
        lineLayer.coreId,
        true
      )
      const lineData = this.linesForSelectedRealEstate.find(
        (line) => line.properties.layerId === lineLayerId
      )
      this.setSelectedLine(lineData.properties)
    },
    async updateAndSetOutdoorZone(zoneLayerId) {
      const zoneLayer = await getZoneLayerById(zoneLayerId)
      if (!zoneLayer) return
      await this.getOutdoorZoneDataForRealEstate({
        categoryId: zoneLayer.categoryId,
        realEstateId: zoneLayer.coreId,
        typeId: zoneLayer.typeId,
        checked: true
      })
      const outdoorZone = this.outdoorZonesForSelectedRealEstate.find(
        (oz) => oz.properties.layerId === zoneLayerId
      )
      this.setSelectedZone(outdoorZone.properties)
    },
    async updateAndSetIndoorZone(zoneLayerId) {
      const zoneLayer = await getZoneLayerById(zoneLayerId)
      if (!zoneLayer) return
      await this.getZoneDataForLevel({
        categoryId: zoneLayer.categoryId,
        levelId: zoneLayer.coreId,
        typeId: zoneLayer.typeId,
        checked: true
      })
      const indoorZone = this.zonesForLevel.find((z) => z.properties.layerId === zoneLayerId)
      this.setSelectedZone(indoorZone.properties)
    },
    async updateAndSetOutdoorPoi(poiId, realEstateId) {
      const poi = await getPoiById(poiId)
      const filteredLayers = this.filteredMapPoiLayers
      const index = filteredLayers.findIndex((layer) => layer.id === poi.categoryId)
      if (index === -1) return
      filteredLayers[index].visible = true
      await this.getOutdoorPoiDataForRealEstate({
        categoryId: poi.categoryId,
        realEstateId: realEstateId,
        typeId: poi.typeId,
        checked: true
      })
      const outdoorPoi = this.outdoorPoisForSelectedRealEstate.find(
        (outdoor) => outdoor.properties.guid === poi.id
      )
      this.setFilteredMapPoiLayers({ filteredPoiLayers: filteredLayers })
      this.dispatchPoi({ poi: outdoorPoi.properties })
    },
    async updateAndSetIndoorPoi(poiId) {
      const poi = await getPoiById(poiId, false)
      const category = this.poiCategoriesForSelectedLevel.find(
        (category) => category.id === poi.categoryId
      )
      if (!category) return
      this.$store.dispatch('planview/getPoisForLevelPerCategory', {
        categoryId: category.id,
        typeId: poi.typeId,
        checked: true,
        levelId: this.selectedLevelId
      })
      poi.categoryName = category.name
      const type = category.types.find((poiType) => poiType.id === poi.typeId)
      poi.color = type?.color ? type.color : '#000'
      this.dispatchPoi({ poi })
    },
    setComplexFromQuery(complexId) {
      const complex = this.complexSource.data.features.find((complex) => complex.id === complexId)
      if (complex) this.dispatchComplex({ complex: complex.properties })
    },
    async setRealEstateFromQuery(realEstateId) {
      const realEstate = this.realEstateSource.data.features.find(
        (realEstate) => realEstate.id === realEstateId
      )
      if (realEstate) await this.dispatchRealEstate({ realEstate: realEstate.properties })
    },
    async setBuildingFromQuery(buildingId) {
      const building = this.buildingSource.data.features.find(
        (building) => building.id === buildingId
      )
      if (building) await this.dispatchBuilding({ building })
    }
  }
}
</script>

<style scoped lang="scss">
@use 'src/design-system/design-tokens/variables.scss';
@import 'src/styles/variables.scss';

.container {
  height: 100%;
  width: 100%;
  z-index: 25;
}

.spinner-overview {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
}

@media screen and (max-width: 1024px) {
  .container {
    height: calc(100% - #{variables.$view-buttons-height});
  }
}
</style>
